import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listServiceRequests, type ServiceRow } from '@/lib/serviceRequests';
import styles from '../partner-jobs/PartnerJobs.module.css';

/**
 * Service calls (Warren, ruling H, 21 October 2026). EVI ops sees every open call (its queue); a partner's team sees the
 * calls on chargers it installed; an SP Manager and their Sales Manager see their partners'. The Api decides the list.
 */
const when = (iso: string) => new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
const tone = (r: ServiceRow) => (r.overdue || r.status === 'with_ops' ? 'warn' : r.status === 'closed' ? 'good' : 'info');

export function ServiceRequestsPage({ onNavigate }: { onNavigate: (to: string) => void }) {
  const [state, setState] = useState<'open' | 'closed'>('open');
  const q = useQuery({ queryKey: ['service-requests', state], queryFn: () => listServiceRequests(state), retry: false, refetchInterval: 60_000 });
  const ops = q.data?.viewer.kind === 'evi_ops', mine = q.data?.viewer.permissions.includes('service:respond');
  const go = (to: string) => (e: React.MouseEvent) => { e.preventDefault(); onNavigate(to); };
  return <div className={styles.page}>
    <div className={styles.heading}>
      <div><h1 className={styles.title}>Service calls</h1>
        <p className={styles.lead}>{ops ? 'Every open service call. EVI ops holds the ones with no installer, the declined ones and the ones not answered in time.'
          : mine ? 'Service calls on chargers you installed. Answer each one: I\'ll handle it, Fixed, Warranty, or I can\'t take it.'
            : 'Service calls on your partners\' chargers. You hear when one passes to EVI ops.'}</p></div>
      <div className={styles.pills} role="group" aria-label="Which calls">
        {(['open', 'closed'] as const).map(s => <button key={s} type="button" className={styles.pill} aria-pressed={state === s} onClick={() => setState(s)}>{s === 'open' ? 'Open' : 'Closed'}</button>)}
      </div>
    </div>
    {q.error && <p role="alert" className={styles.message}>{q.error.message}</p>}
    {!q.data ? <p role="status" className={styles.message}>Loading service calls</p> : <section className={styles.card} aria-label="Service calls">
      {q.data.rows.length === 0 ? <p className={styles.hint}>{state === 'open' ? 'No open service calls.' : 'No closed service calls yet.'}</p>
        : <ul className={styles.list}>{q.data.rows.map(r => <li key={r.id}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
            <a href={`/service-requests/${r.id}`} onClick={go(`/service-requests/${r.id}`)}>{r.location.name}, charger {r.charger_identity}</a>
            <span className={styles.sub}>{r.problem}</span>
            <span className={styles.sub}>{[`Opened ${when(r.opened_at)}`, r.installer && !mine ? `Installer: ${r.installer}` : r.responder === 'evi_ops' && !r.installer ? 'No installer of record' : null,
              r.due_label && r.status === 'open' ? `Answer by ${r.due_label}` : null].filter(Boolean).join(' · ')}</span>
          </div>
          <span className={styles.status} data-tone={tone(r)}>{r.overdue ? 'Past its answer time' : r.status_label}</span>
        </li>)}</ul>}
    </section>}
  </div>;
}
